/**
 * Centralized route map — Single source of truth for EN ↔ ES route equivalents.
 * Import this instead of duplicating route maps across components.
 */

export const ROUTE_MAP_EN_TO_ES: Record<string, string> = {
  '/': '/es',
  '/how-it-works': '/es/como-funciona',
  '/plans': '/es/planes',
  '/industries': '/es/industrias',
  '/start-your-llc': '/es/inicia-tu-llc',
  '/about': '/es/sobre-nosotros',
  '/blog': '/es/blog',
  '/contact': '/es/contacto',
  '/thank-you': '/es/gracias',
  '/why-us': '/es/por-que-nosotros',
  '/results': '/es/resultados',
  '/faq': '/es/preguntas-frecuentes',
  '/privacy-policy': '/es/politica-de-privacidad',
  '/terms-of-service': '/es/terminos-de-servicio',
  '/disclaimer': '/es/aviso-legal',
};

export const ROUTE_MAP_ES_TO_EN: Record<string, string> = Object.fromEntries(
  Object.entries(ROUTE_MAP_EN_TO_ES).map(([en, es]) => [es, en])
);

/**
 * Given the current pathname, returns the equivalent path in the OTHER language.
 * Used by the language toggle button.
 */
export function getTogglePath(currentPath: string): string {
  const isEs = currentPath.startsWith('/es');

  if (isEs) {
    // Blog post: /es/blog/:slug → /blog/:slug
    const blogMatch = currentPath.match(/^\/es\/blog\/(.+)$/);
    if (blogMatch) return `/blog/${blogMatch[1]}`;
    // Map lookup
    if (ROUTE_MAP_ES_TO_EN[currentPath]) return ROUTE_MAP_ES_TO_EN[currentPath];
    // Fallback: strip /es prefix
    const stripped = currentPath.replace(/^\/es(\/|$)/, '/').replace(/\/$/, '');
    return stripped || '/';
  } else {
    // Blog post: /blog/:slug → /es/blog/:slug
    const blogMatch = currentPath.match(/^\/blog\/(.+)$/);
    if (blogMatch) return `/es/blog/${blogMatch[1]}`;
    // Map lookup
    if (ROUTE_MAP_EN_TO_ES[currentPath]) return ROUTE_MAP_EN_TO_ES[currentPath];
    // Fallback: prepend /es
    return '/es';
  }
}

/**
 * Returns a language-aware path.
 * Pass the EN version of the path and it resolves to ES if lang is 'es'.
 */
export function lp(enPath: string, lang: 'en' | 'es'): string {
  if (lang === 'es') return ROUTE_MAP_EN_TO_ES[enPath] ?? enPath;
  return enPath;
}

/**
 * Returns 'es' or 'en' based on the current URL path.
 * More reliable than reading i18n.language directly.
 */
export function getLangFromPath(pathname: string): 'es' | 'en' {
  return pathname.startsWith('/es') ? 'es' : 'en';
}
